import { useEffect, useState } from "react";
import type { ReactNode } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  tableBodyClasses,
  tableCellClasses,
  tableContainerClasses,
  tableRowClasses,
} from "@mui/material";
import MandatSdeaTableRow from "./MandatSdeaTableRow";
import type { Mandat } from "models";
import { elusUtil } from "utils";
import type Competence from "constants/Competence";
import competenceMap from "constants/CompetenceMap";
import MandatInstitutionnelTableRow from "./MandatInstitutionnelTableRow";
import { Authorization, dateUtil } from "@sdeapps/react-core";
import NewMandatSdeaTableRowWithErrorBoundary from "./NewMandatSdeaTableRow";
import { ApplicationRole } from "constants/ApplicationRole";
import { MandatDataProvider } from "pages/Elu/providers/MandatDataProvider";
import { sortUtil } from "utils/sortUtil";

interface MandatsInformationsListProps {
  mandats?: Array<Mandat>;
  typeMandats: "SDEA" | "institutionnels";
  noMandatsFallback?: ReactNode;
  cancelDisplayCreateNewMandat?: () => void;
  refetchElu?: () => void;
  displayCreateNewMandat?: boolean;
  createMandatEluId?: string;
}

function MandatsInformationsList({
  mandats,
  typeMandats,
  noMandatsFallback,
  cancelDisplayCreateNewMandat,
  refetchElu,
  displayCreateNewMandat = false,
  createMandatEluId,
}: Readonly<MandatsInformationsListProps>): ReactNode {
  const [orderedMandats, setOrderedMandats] = useState<Array<Mandat>>([]);

  useEffect(() => {
    if (mandats != null) {
      setOrderedMandats(
        mandats.toSorted(
          sortUtil.combineMultipleSort(
            elusUtil.sortMandatsByImportance,
            (a, b) =>
              // eslint-disable-next-line @typescript-eslint/non-nullable-type-assertion-style
              (competenceMap.get(a?.competence as Competence)?.index ?? 0) -
              // eslint-disable-next-line @typescript-eslint/non-nullable-type-assertion-style
              (competenceMap.get(b?.competence as Competence)?.index ?? 0),
            (a, b) =>
              `${a.libelleTerritoire}${a.libellePerimetre}${a.libelleCollectivite}`.localeCompare(
                `${b.libelleTerritoire}${b.libellePerimetre}${b.libelleCollectivite}`
              ),
            (a, b) => dateUtil.compareDesc(a.dateDebutMandat, b.dateDebutMandat)
          )
        )
      );
    } else {
      setOrderedMandats([]);
    }
  }, [mandats]);

  if (orderedMandats.length === 0 && !displayCreateNewMandat) {
    return (
      <Typography>
        {noMandatsFallback ?? "Cette personne ne possède aucun mandat de ce type."}
      </Typography>
    );
  }

  const mandatsTableRows = orderedMandats.map((mandat: Mandat) =>
    typeMandats === "SDEA" ? (
      <MandatSdeaTableRow
        key={`${mandat.libelleFonction}${mandat.competence}${mandat.codeCollectivite}${mandat.idPerimetre}${mandat.idTerritoire}${mandat.dateDebutMandat}${mandat.dateFinMandat}`}
        mandatSdea={mandat}
        refetchElu={refetchElu}
      />
    ) : (
      <MandatInstitutionnelTableRow
        key={`${mandat.codeCollectivite}${mandat.libelleFonction}${mandat.dateDebutMandat}`}
        mandat={mandat}
      />
    )
  );

  return (
    <TableContainer
      sx={{
        [`&.${tableContainerClasses.root} .${tableBodyClasses.root} .${tableRowClasses.root}:last-child .${tableCellClasses.root}`]:
          { border: "none" },
      }}>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>
              <Typography sx={{ fontWeight: 600 }}>Fonction</Typography>
            </TableCell>
            {typeMandats === "SDEA" && (
              <TableCell>
                <Typography sx={{ fontWeight: 600 }}>Compétence</Typography>
              </TableCell>
            )}
            <TableCell>
              <Typography sx={{ fontWeight: 600 }}>Collectivité</Typography>
            </TableCell>
            <TableCell>
              <Typography sx={{ fontWeight: 600 }}>Période</Typography>
            </TableCell>
            {typeMandats === "SDEA" && (orderedMandats.length > 0 || displayCreateNewMandat) && (
              <TableCell padding="none" />
            )}
          </TableRow>
        </TableHead>

        <Authorization
          roles={[ApplicationRole.ADMINISTRATOR, ApplicationRole.GESTIONNAIRE_ELECTIONS]}
          fallback={<TableBody>{mandatsTableRows}</TableBody>}>
          <MandatDataProvider>
            <TableBody>
              {mandatsTableRows}
              {displayCreateNewMandat && createMandatEluId != null && (
                <NewMandatSdeaTableRowWithErrorBoundary
                  idElu={createMandatEluId}
                  cancel={cancelDisplayCreateNewMandat}
                  refetchElu={refetchElu}
                />
              )}
            </TableBody>
          </MandatDataProvider>
        </Authorization>
      </Table>
    </TableContainer>
  );
}

export default MandatsInformationsList;
